/**
 * Endpoint (source / destination) picker for the create wizard. Given an
 * `EndpointKind`, loads the matching option list — suppliers or locations
 * (customers are entered free-text, since there is no warehouse customer list).
 * One dedicated sub-component per kind so only the relevant list is fetched.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useLocations, useSuppliers } from '@/api/capacities';
import type { EndpointKind } from './process-type-options';

interface EndpointSelectProps {
  label: string;
  kind: EndpointKind;
  value: string;
  required?: boolean;
  error?: string;
  /** Emits both the id (payload) and the display label (review). */
  onChange: (value: string, displayName: string) => void;
}

interface Option {
  label: string;
  value: string;
}

/** Shared Select shell over a resolved option list. */
function OptionSelect({
  label,
  placeholder,
  options,
  value,
  required,
  error,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: Option[];
  value: string;
  required?: boolean;
  error?: string;
  onChange: (value: string, displayName: string) => void;
}) {
  return (
    <Field label={label} required={required} error={error}>
      {(p) => (
        <Select
          value={value || undefined}
          onValueChange={(v) => onChange(v, options.find((o) => o.value === v)?.label ?? v)}
        >
          <SelectTrigger {...p}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}

function SupplierSelect(props: Omit<EndpointSelectProps, 'kind'>) {
  const { data } = useSuppliers({ page_size: 200 });
  const options = (data?.results ?? []).map((s) => ({ label: s.name, value: s.id }));
  return <OptionSelect {...props} placeholder="Select supplier" options={options} />;
}

function LocationSelect(props: Omit<EndpointSelectProps, 'kind'>) {
  const { data } = useLocations({ page_size: 200 });
  const options = (data?.results ?? []).map((l) => ({ label: l.name, value: l.id }));
  return <OptionSelect {...props} placeholder="Select location" options={options} />;
}

export function EndpointSelect({ label, kind, value, required, error, onChange }: EndpointSelectProps) {
  if (kind === 'supplier') {
    return <SupplierSelect label={label} value={value} required={required} error={error} onChange={onChange} />;
  }
  if (kind === 'location') {
    return <LocationSelect label={label} value={value} required={required} error={error} onChange={onChange} />;
  }
  // Customers have no lookup list in the warehouse service → free-text entry.
  return (
    <Field label={label} required={required} error={error}>
      {(p) => (
        <Input
          {...p}
          value={value}
          onChange={(e) => onChange(e.target.value, e.target.value)}
          placeholder="Customer name"
        />
      )}
    </Field>
  );
}
